import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Bell, Plus, Trash2, Edit3, Save, X, CheckCircle2, AlertTriangle, Loader2, Clock, History, RefreshCw, Filter, Lock } from "lucide-react";
import ConfirmDialog from "@/components/ConfirmDialog";
import { fmtDateTime } from "@/lib/format";
import { useAuth } from "@/lib/auth";

const SHEETS = [
  { value: "TTF", label: "Forward Gas TTF", unit: "€/MWh" },
  { value: "PSV", label: "Forward Gas PSV", unit: "€/MWh" },
  { value: "Power", label: "Forward Power", unit: "€/MWh" },
  { value: "CO2", label: "Forward CO2", unit: "€/tCO2" },
];

const DEFAULT_FORM = {
  source_sheet: "TTF",
  contract_name: "",
  direction: "above",
  threshold: "",
  channel_push: true,
  channel_email: false,
  enabled: true,
  expires_at: "",
};

// Formatta una data ISO "YYYY-MM-DD" o full-ISO in gg/mm/aaaa (Italia)
const fmtDateIt = (v) => {
  if (!v) return "—";
  const s = String(v).slice(0, 10);
  const parts = s.split("-");
  return parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : s;
};

/**
 * Componente riusabile: form + lista alert.
 * Parametri:
 *   - `targetUserId`  → filtra la lista per destinatario. Nel caso client-managed
 *                       filtra su `user_id`. Ignorato lato admin_managed.
 *   - `adminManaged`  → il form crea alert admin_managed (backoffice) invece che client.
 *   - `referencedClientId` → tag cliente di contesto per gli alert admin_managed.
 *   - `listScope`     → 'admin' | 'client' | undefined per non filtrare (staff only).
 *   - `readonly`      → nasconde il form (usato per la tab "Alert dei clienti" in backoffice).
 */
export function PriceAlertsPanel({
  targetUserId = null,
  adminManaged = false,
  referencedClientId = null,
  listScope = undefined,
  readonly = false,
}) {
  const [alerts, setAlerts] = useState([]);
  const [history, setHistory] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmState, setConfirmState] = useState(null);
  const [activeTab, setActiveTab] = useState("active"); // "active" | "history"
  // Filtri storico
  const [histContract, setHistContract] = useState("");
  const [histDateFrom, setHistDateFrom] = useState("");
  const [histDateTo, setHistDateTo] = useState("");
  // Rinnovo alert
  const [renewingId, setRenewingId] = useState(null);
  const [renewDate, setRenewDate] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = { status: "active" };
      if (targetUserId) params.user_id = targetUserId;
      if (listScope) params.scope = listScope;
      const { data } = await api.get("/price-alerts", { params });
      setAlerts(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore caricamento alert");
    } finally {
      setLoading(false);
    }
  };
  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const params = { status: "history" };
      if (targetUserId) params.user_id = targetUserId;
      if (listScope) params.scope = listScope;
      if (histContract) params.contract_name = histContract;
      if (histDateFrom) params.date_from = histDateFrom;
      if (histDateTo) params.date_to = histDateTo;
      const { data } = await api.get("/price-alerts", { params });
      setHistory(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore caricamento storico");
    } finally {
      setHistoryLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [targetUserId, listScope]);
  useEffect(() => {
    if (activeTab === "history") loadHistory();
    // eslint-disable-next-line
  }, [activeTab, histContract, histDateFrom, histDateTo, targetUserId, listScope]);

  // Load contracts on sheet change
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/price-alerts/contracts", {
          params: { source_sheet: form.source_sheet },
        });
        setContracts(data.contracts || []);
        // Se il contract attualmente selezionato non è nella lista → reset
        if (form.contract_name && !(data.contracts || []).includes(form.contract_name)) {
          setForm((prev) => ({ ...prev, contract_name: "" }));
        }
      } catch (e) {
        setContracts([]);
      }
    })();
    // eslint-disable-next-line
  }, [form.source_sheet]);

  const currentUnit = SHEETS.find((s) => s.value === form.source_sheet)?.unit || "€/MWh";

  const resetForm = () => { setForm(DEFAULT_FORM); setEditingId(null); };

  const submit = async () => {
    if (!form.contract_name) return toast.error("Seleziona un contratto");
    const th = parseFloat(form.threshold);
    if (isNaN(th) || th <= 0) return toast.error("Inserisci una soglia numerica > 0");
    setBusy(true);
    try {
      const payload = {
        source_sheet: form.source_sheet,
        contract_name: form.contract_name,
        direction: form.direction,
        threshold: th,
        channel_push: form.channel_push,
        channel_email: form.channel_email,
        enabled: form.enabled,
        expires_at: form.expires_at || null,
      };
      if (editingId) {
        // Per l'edit il payload potrebbe cambiare anche il referenced_client_id
        if (adminManaged) payload.referenced_client_id = referencedClientId || null;
        await api.patch(`/price-alerts/${editingId}`, payload);
        toast.success("Alert aggiornato");
      } else {
        if (adminManaged) {
          payload.admin_managed = true;
          if (referencedClientId) {
            payload.user_id = referencedClientId; // interpretato come referenced_client_id lato backend
            payload.referenced_client_id = referencedClientId;
          }
        } else if (targetUserId) {
          payload.user_id = targetUserId;
        }
        await api.post("/price-alerts", payload);
        toast.success("Alert creato");
      }
      resetForm();
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore salvataggio");
    } finally {
      setBusy(false);
    }
  };

  const startEdit = (a) => {
    setForm({
      source_sheet: a.source_sheet,
      contract_name: a.contract_name,
      direction: a.direction,
      threshold: String(a.threshold),
      channel_push: !!a.channel_push,
      channel_email: !!a.channel_email,
      enabled: !!a.enabled,
      expires_at: a.expires_at ? String(a.expires_at).slice(0, 10) : "",
    });
    setEditingId(a.id);
    setActiveTab("active");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const doRenew = async (a) => {
    try {
      const payload = renewDate ? { expires_at: renewDate } : {};
      await api.post(`/price-alerts/${a.id}/renew`, payload);
      toast.success("Alert rinnovato");
      setRenewingId(null);
      setRenewDate("");
      await Promise.all([load(), loadHistory()]);
      setActiveTab("active");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore rinnovo");
    }
  };

  const removeAlert = (a) => {
    setConfirmState({
      title: "Eliminare l'alert?",
      message: `${a.source_sheet} ${a.contract_name} — soglia ${a.threshold}. Non ricevi più notifiche per questa condizione.`,
      confirmLabel: "Elimina",
      variant: "danger",
      onConfirm: async () => {
        try {
          await api.delete(`/price-alerts/${a.id}`);
          toast.success("Alert eliminato");
          await load();
        } catch (e) {
          toast.error(e.response?.data?.detail || "Errore eliminazione");
        }
      },
    });
  };

  const toggleEnabled = async (a) => {
    try {
      await api.patch(`/price-alerts/${a.id}`, { enabled: !a.enabled });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    }
  };

  const closeAlert = (a) => {
    setConfirmState({
      title: "Chiudere l'alert?",
      message: `${a.source_sheet} ${a.contract_name} — soglia ${a.threshold}. L'alert viene spostato nello Storico e potrai rinnovarlo in qualsiasi momento.`,
      confirmLabel: "Chiudi alert",
      variant: "default",
      onConfirm: async () => {
        try {
          await api.post(`/price-alerts/${a.id}/close`);
          toast.success("Alert chiuso");
          await Promise.all([load(), loadHistory()]);
        } catch (e) {
          toast.error(e.response?.data?.detail || "Errore chiusura");
        }
      },
    });
  };

  return (
    <div className="space-y-6" data-testid="price-alerts-panel">
      {/* Form crea/modifica (nascosto in readonly) */}
      {!readonly && (
      <div className="card-elevated p-5">
        <div className="flex items-center gap-2 mb-4">
          <Bell className="text-blue-600" size={18} />
          <h2 className="font-display font-bold text-slate-900">
            {editingId ? "Modifica alert" : "Nuovo alert soglia prezzo"}
          </h2>
          {editingId && (
            <button
              onClick={resetForm}
              className="ml-auto text-xs text-slate-500 hover:text-red-600 flex items-center gap-1"
              data-testid="alert-cancel-edit"
            ><X size={13} /> Annulla modifica</button>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="text-[11px] uppercase tracking-widest font-semibold text-slate-500">Curva forward</label>
            <select
              value={form.source_sheet}
              onChange={(e) => setForm({ ...form, source_sheet: e.target.value, contract_name: "" })}
              className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm bg-white outline-none focus:border-blue-500"
              data-testid="alert-sheet-select"
            >
              {SHEETS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest font-semibold text-slate-500">Contratto</label>
            <select
              value={form.contract_name}
              onChange={(e) => setForm({ ...form, contract_name: e.target.value })}
              className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm bg-white outline-none focus:border-blue-500"
              data-testid="alert-contract-select"
            >
              <option value="">— seleziona —</option>
              {contracts.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <div className="text-[10px] text-slate-400 mt-1">
              {contracts.length} contratt{contracts.length === 1 ? "o" : "i"} disponibili
            </div>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest font-semibold text-slate-500">Direzione</label>
            <div className="mt-1 inline-flex items-center bg-slate-100 rounded-lg p-1 shadow-inner w-full">
              <button
                onClick={() => setForm({ ...form, direction: "above" })}
                className={`flex-1 px-3 py-1.5 rounded-md text-xs font-semibold ${form.direction === "above" ? "bg-white shadow text-red-600" : "text-slate-500"}`}
                data-testid="alert-direction-above"
              >≥ Soglia (sale)</button>
              <button
                onClick={() => setForm({ ...form, direction: "below" })}
                className={`flex-1 px-3 py-1.5 rounded-md text-xs font-semibold ${form.direction === "below" ? "bg-white shadow text-emerald-600" : "text-slate-500"}`}
                data-testid="alert-direction-below"
              >≤ Soglia (scende)</button>
            </div>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest font-semibold text-slate-500">Soglia ({currentUnit})</label>
            <input
              type="number"
              step="0.001"
              value={form.threshold}
              onChange={(e) => setForm({ ...form, threshold: e.target.value })}
              placeholder={`Es. ${form.source_sheet === "CO2" ? "80" : "45"}`}
              className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-blue-500"
              data-testid="alert-threshold-input"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 flex items-center gap-1">
              <Clock size={11} /> Scadenza (opzionale)
            </label>
            <input
              type="date"
              value={form.expires_at || ""}
              onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
              min={new Date().toISOString().slice(0, 10)}
              className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-blue-500"
              data-testid="alert-expires-input"
            />
            <div className="text-[10px] text-slate-400 mt-1">
              Dopo questa data l&apos;alert viene chiuso automaticamente. Lascia vuoto per nessuna scadenza.
            </div>
          </div>
        </div>
        <div className="flex items-center gap-4 mt-4 flex-wrap">
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={form.channel_push} onChange={(e) => setForm({ ...form, channel_push: e.target.checked })} data-testid="alert-channel-push" />
            Notifica push in-app
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={form.channel_email} onChange={(e) => setForm({ ...form, channel_email: e.target.checked })} data-testid="alert-channel-email" />
            Email
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} data-testid="alert-enabled-toggle" />
            Attivo
          </label>
          <button
            onClick={submit}
            disabled={busy}
            className="btn-brand ml-auto flex items-center gap-2 text-sm disabled:opacity-60"
            data-testid="alert-submit"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : (editingId ? <Save size={14} /> : <Plus size={14} />)}
            {editingId ? "Salva modifiche" : "Crea alert"}
          </button>
        </div>
        <div className="mt-3 text-[11px] text-slate-500 leading-relaxed">
          L&apos;alert scatta al <b>primo cross</b> della soglia (usando Ask, Last o Previous Close se non quotato).
          Al primo trigger viene chiuso e spostato nello <b>Storico</b>: da lì puoi <b>rinnovarlo</b> con un clic (opzionalmente con una nuova scadenza).
        </div>
        <div className="mt-2 text-[11px] text-blue-700 bg-blue-50 border border-blue-100 rounded-md px-3 py-2 flex items-center gap-1.5" data-testid="alert-max-hint">
          <Bell size={11} className="text-blue-600 flex-shrink-0" />
          Puoi impostare al massimo <b className="mx-0.5">4 alert attivi</b> contemporaneamente. Se raggiungi il limite, chiudi un alert dallo Storico prima di crearne uno nuovo.
        </div>
      </div>
      )}

      {/* Tabs Attivi / Storico */}
      <div className="card-elevated overflow-hidden">
        <div className="px-4 pt-3 border-b border-slate-100 flex items-center gap-1 flex-wrap">
          <button
            onClick={() => setActiveTab("active")}
            className={`px-3 py-2 text-sm font-semibold rounded-t-md flex items-center gap-2 ${activeTab === "active" ? "text-blue-600 border-b-2 border-blue-600 -mb-[1px]" : "text-slate-500 hover:text-slate-700"}`}
            data-testid="alerts-tab-active"
          >
            <Bell size={14} /> Attivi <span className="text-xs font-normal">({alerts.length})</span>
          </button>
          <button
            onClick={() => setActiveTab("history")}
            className={`px-3 py-2 text-sm font-semibold rounded-t-md flex items-center gap-2 ${activeTab === "history" ? "text-blue-600 border-b-2 border-blue-600 -mb-[1px]" : "text-slate-500 hover:text-slate-700"}`}
            data-testid="alerts-tab-history"
          >
            <History size={14} /> Storico
          </button>
        </div>

        {activeTab === "history" && (
          <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2 flex-wrap bg-slate-50/50">
            <Filter size={14} className="text-slate-400" />
            <select
              value={histContract}
              onChange={(e) => setHistContract(e.target.value)}
              className="px-2 py-1.5 rounded-lg border border-slate-200 text-xs bg-white outline-none focus:border-blue-500"
              data-testid="alert-hist-contract"
            >
              <option value="">Contratto: tutti</option>
              {Array.from(new Set(history.map((h) => h.contract_name))).sort().map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
            <input
              type="date"
              value={histDateFrom}
              onChange={(e) => setHistDateFrom(e.target.value)}
              className="px-2 py-1.5 rounded-lg border border-slate-200 text-xs bg-white outline-none focus:border-blue-500"
              data-testid="alert-hist-date-from"
              placeholder="Da"
            />
            <input
              type="date"
              value={histDateTo}
              onChange={(e) => setHistDateTo(e.target.value)}
              className="px-2 py-1.5 rounded-lg border border-slate-200 text-xs bg-white outline-none focus:border-blue-500"
              data-testid="alert-hist-date-to"
              placeholder="A"
            />
            {(histContract || histDateFrom || histDateTo) && (
              <button
                onClick={() => { setHistContract(""); setHistDateFrom(""); setHistDateTo(""); }}
                className="text-xs text-slate-500 hover:text-red-600 flex items-center gap-1 ml-1"
              >
                <X size={12} /> Pulisci
              </button>
            )}
            <span className="ml-auto text-xs text-slate-400">{history.length} risultati</span>
          </div>
        )}

        {activeTab === "active" ? (
          loading ? (
            <div className="p-10 text-center text-sm text-slate-400">Caricamento…</div>
          ) : alerts.length === 0 ? (
            <div className="p-10 text-center text-sm text-slate-500" data-testid="alerts-empty">
              Nessun alert configurato. Aggiungine uno dal form qui sopra.
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {alerts.map((a) => renderAlertRow(a, false))}
            </ul>
          )
        ) : (
          historyLoading ? (
            <div className="p-10 text-center text-sm text-slate-400">Caricamento storico…</div>
          ) : history.length === 0 ? (
            <div className="p-10 text-center text-sm text-slate-500" data-testid="alerts-history-empty">
              Nessun alert nello storico.
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {history.map((a) => renderAlertRow(a, true))}
            </ul>
          )
        )}
      </div>
      <ConfirmDialog state={confirmState} onClose={() => setConfirmState(null)} />
    </div>
  );

  function renderAlertRow(a, isHistory) {
    const unit = SHEETS.find((s) => s.value === a.source_sheet)?.unit || "€/MWh";
    const dirColor = a.direction === "above" ? "text-red-600" : "text-emerald-600";
    const dirSign = a.direction === "above" ? "≥" : "≤";
    const isTriggered = a.state === "triggered";
    const reason = a.closed_reason || "";
    const reasonBadge = reason === "fired" ? { text: "Scattato", cls: "bg-red-100 text-red-700" }
      : reason === "expired" ? { text: "Scaduto", cls: "bg-amber-100 text-amber-700" }
      : reason === "manually_disabled" || reason === "admin_disabled" ? { text: "Chiuso", cls: "bg-slate-200 text-slate-700" }
      : null;
    return (
      <li key={a.id} className={`px-5 py-3 flex items-center gap-3 flex-wrap ${!isHistory && !a.enabled ? "opacity-50" : ""}`} data-testid={`alert-row-${a.id}`}>
        <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${isTriggered ? "bg-red-100 text-red-600" : isHistory ? "bg-slate-100 text-slate-400" : "bg-slate-100 text-slate-500"}`}>
          {isTriggered ? <AlertTriangle size={16} /> : <Bell size={16} />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-slate-900 flex items-center gap-2 flex-wrap">
            <span>{a.source_sheet} · {a.contract_name}</span>
            {a.admin_managed && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 font-bold uppercase tracking-wider">Staff</span>
            )}
            {reasonBadge && (
              <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold uppercase tracking-wider ${reasonBadge.cls}`}>{reasonBadge.text}</span>
            )}
            {a.admin_managed && (a.referenced_client_company_name || a.referenced_client_full_name) && (
              <span className="text-xs font-normal text-slate-500">
                — cliente: {a.referenced_client_company_name || a.referenced_client_full_name}
              </span>
            )}
            {!a.admin_managed && a.user_email && (
              <span className="text-xs font-normal text-slate-500">
                — {a.user_full_name || a.user_email}
                {a.user_company_name && <span className="text-slate-400"> · {a.user_company_name}</span>}
              </span>
            )}
          </div>
          <div className="text-xs text-slate-500 flex items-center gap-3 flex-wrap mt-0.5">
            <span className={`font-semibold ${dirColor}`}>{dirSign} {a.threshold} {unit}</span>
            <span className="flex items-center gap-1">
              {a.channel_push && <span className="text-blue-600">push</span>}
              {a.channel_email && (
                <span className="text-purple-600">
                  email{a.admin_managed && a.email_recipient ? ` → ${a.email_recipient}` : ""}
                </span>
              )}
            </span>
            <span className="text-slate-400" data-testid={`alert-created-${a.id}`}>
              creato: {fmtDateIt(a.created_at)}
              {a.created_by_full_name && <span> · da <b className="text-slate-600">{a.created_by_full_name}</b></span>}
            </span>
            {a.expires_at && !isHistory && (
              <span className="text-amber-600 flex items-center gap-1" data-testid={`alert-expires-${a.id}`}>
                <Clock size={11} /> scade il {fmtDateIt(a.expires_at)}
              </span>
            )}
            {a.closed_at && (
              <span className="text-slate-500">
                chiuso: {fmtDateTime(a.closed_at)}
                {reason === "fired" && a.last_triggered_value != null && <span> @ {a.last_triggered_value}</span>}
              </span>
            )}
            {!isHistory && isTriggered && (
              <span className="text-red-600 flex items-center gap-1">
                <CheckCircle2 size={11} /> Scattato · {fmtDateTime(a.last_triggered_at)} @ {a.last_triggered_value}
              </span>
            )}
            {!isHistory && !a.enabled && <span className="text-slate-400">· disattivato</span>}
          </div>
          {isHistory && renewingId === a.id && (
            <div className="mt-2 flex items-center gap-2 flex-wrap bg-blue-50 border border-blue-100 rounded-lg p-2" data-testid={`alert-renew-panel-${a.id}`}>
              <span className="text-xs text-slate-600">Nuova scadenza (opzionale):</span>
              <input
                type="date"
                value={renewDate}
                onChange={(e) => setRenewDate(e.target.value)}
                min={new Date().toISOString().slice(0, 10)}
                className="px-2 py-1 rounded border border-slate-200 text-xs outline-none focus:border-blue-500"
                data-testid={`alert-renew-date-${a.id}`}
              />
              <button
                onClick={() => doRenew(a)}
                className="text-xs px-2 py-1 rounded bg-blue-600 text-white hover:bg-blue-700"
                data-testid={`alert-renew-confirm-${a.id}`}
              >Conferma rinnovo</button>
              <button
                onClick={() => { setRenewingId(null); setRenewDate(""); }}
                className="text-xs px-2 py-1 rounded border border-slate-200 text-slate-500 hover:text-red-600"
              >Annulla</button>
            </div>
          )}
        </div>
        {!readonly && !isHistory && (
          <>
            <button
              onClick={() => closeAlert(a)}
              className="text-xs px-2 py-1 rounded border border-slate-200 text-slate-600 hover:border-amber-300 hover:text-amber-600"
              data-testid={`alert-close-${a.id}`}
            >
              Chiudi
            </button>
            <button
              onClick={() => startEdit(a)}
              className="p-1.5 text-slate-500 hover:text-blue-600"
              title="Modifica"
              data-testid={`alert-edit-${a.id}`}
            ><Edit3 size={14} /></button>
            <button
              onClick={() => removeAlert(a)}
              className="p-1.5 text-red-500 hover:text-red-700"
              title="Elimina definitivamente"
              data-testid={`alert-delete-${a.id}`}
            ><Trash2 size={14} /></button>
          </>
        )}
        {!readonly && isHistory && renewingId !== a.id && (
          <button
            onClick={() => { setRenewingId(a.id); setRenewDate(""); }}
            className="text-xs px-2 py-1 rounded border border-blue-200 text-blue-600 hover:bg-blue-50 flex items-center gap-1"
            data-testid={`alert-renew-${a.id}`}
          >
            <RefreshCw size={12} /> Rinnova
          </button>
        )}
      </li>
    );
  }
}

export default function PriceAlerts() {
  const { user } = useAuth();
  const isStaff = user?.role === "admin" || user?.role === "editor";
  const disabled = !isStaff && user?.price_alerts_enabled === false;
  if (disabled) {
    return (
      <div className="space-y-6" data-testid="price-alerts-disabled">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-3">
            <Bell className="text-slate-400" size={26} /> Alert Prezzi
          </h1>
        </div>
        <div className="card-elevated p-8 text-center">
          <div className="w-12 h-12 mx-auto rounded-full bg-slate-100 flex items-center justify-center mb-3">
            <Lock size={22} className="text-slate-400" />
          </div>
          <div className="font-display text-lg font-semibold text-slate-800">Modulo non abilitato</div>
          <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
            Gli Alert Prezzi non sono attivi per la tua azienda. Contatta il tuo referente Adybo per abilitarli.
          </p>
        </div>
      </div>
    );
  }
  return (
    <div className="space-y-6" data-testid="price-alerts-page">
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-3">
          <Bell className="text-blue-600" size={26} /> Alert Prezzi
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Ricevi una notifica quando un contratto Forward supera una soglia che imposti tu.
          Coperti: Forward Gas TTF/PSV, Forward Power, Forward CO2.
        </p>
      </div>
      <PriceAlertsPanel />
    </div>
  );
}
